import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * Hook that provides a temporary flag that auto-resets after a duration.
 *
 * Useful for preventing animations during state changes, showing temporary UI states,
 * or any scenario where you need a flag that automatically resets.
 *
 * Features:
 * - Proper cleanup on unmount
 * - Handles multiple calls (clears previous timeout)
 *
 * @param duration - How long the flag stays true (in milliseconds)
 * @returns [isActive, setTemporarily] - Current flag state and function to activate it
 *
 * @example
 * const [isNavigating, setNavigating] = useTemporaryFlag(50)
 *
 * const handleClick = () => {
 *   setNavigating() // Flag becomes true for 50ms
 *   // ... other logic
 * }
 */
export function useTemporaryFlag(duration: number): [boolean, () => void] {
  const [isActive, setIsActive] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setTemporarily = useCallback(() => {
    // Clear any existing timeout to prevent stale resets
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setIsActive(true)

    timeoutRef.current = setTimeout(() => {
      setIsActive(false)
      timeoutRef.current = null
    }, duration)
  }, [duration])

  // Cleanup on unmount
  useEffect(() => {
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return [isActive, setTemporarily]
}
